<template>
  <span class="ai-prompt-optimizer" :class="{ 'has-undo': canUndo }">
    <button
      class="optimize-button"
      type="button"
      :disabled="!canOptimize"
      :aria-busy="String(optimizing)"
      :aria-label="optimizing ? 'AI 正在优化提示词' : cooldown ? '提示词优化按钮暂时锁定' : '使用 AI 优化当前提示词'"
      :title="empty ? '请先填写需要优化的内容' : '补充执行细节并保留原始意图'"
      @click.prevent="optimize"
    >
      <i :class="optimizing ? 'el-icon-loading' : cooldown ? 'el-icon-circle-check' : 'el-icon-magic-stick'" aria-hidden="true"></i>
      <span>{{ optimizing ? '优化中…' : cooldown ? '请稍候' : 'AI 优化提示词' }}</span>
    </button>
    <button
      v-if="canUndo"
      class="undo-button"
      type="button"
      :disabled="disabled || optimizing"
      aria-label="撤销本次 AI 提示词优化"
      @click.prevent="undo"
    >
      撤销
    </button>
    <span class="status-text visually-hidden" role="status" aria-live="polite">{{ statusText }}</span>
  </span>
</template>

<script>
import { apiRequest } from '@/services/api'

export default {
  name: 'AiPromptOptimizer',
  props: {
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    maxLength: { type: Number, default: 300 },
    context: { type: String, default: '室内设计图片生成或编辑' },
    imageUrls: { type: Array, default: () => [] },
    conversationId: { type: String, default: '' }
  },
  data() {
    return {
      optimizing: false,
      originalValue: '',
      optimizedValue: '',
      applyingValue: false,
      statusText: '',
      cooldown: false,
      cooldownTimer: null
    }
  },
  computed: {
    empty() {
      return !String(this.value || '').trim()
    },
    canOptimize() {
      return !this.disabled && !this.optimizing && !this.cooldown && !this.empty
    },
    canUndo() {
      return Boolean(this.originalValue) && this.value === this.optimizedValue
    },
    safeMaxLength() {
      return Math.max(80, Math.min(1200, Number(this.maxLength) || 300))
    }
  },
  watch: {
    value(nextValue) {
      if (this.applyingValue) return
      if (!nextValue || nextValue !== this.optimizedValue) this.clearUndo()
    }
  },
  beforeDestroy() {
    if (this.cooldownTimer) window.clearTimeout(this.cooldownTimer)
  },
  methods: {
    clearUndo() {
      this.originalValue = ''
      this.optimizedValue = ''
    },
    notify(message, type = 'info') {
      if (this.$message && typeof this.$message[type] === 'function') this.$message[type](message)
    },
    async optimize() {
      if (!this.canOptimize) return
      const sourceValue = String(this.value || '')
      const sourcePrompt = sourceValue.trim()
      this.optimizing = true
      this.statusText = 'AI 正在优化提示词'
      this.$emit('loading', true)
      try {
        const result = await apiRequest('/api/playground/optimize-prompt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: sourcePrompt,
            imageUrls: this.imageUrls.filter(Boolean).slice(0, 6),
            conversationId: this.conversationId || null,
            context: this.context,
            maxLength: this.safeMaxLength
          }),
          timeoutMs: 120000,
          timeoutMessage: 'AI 优化提示词超时，请稍后重试',
          retries: 1,
          retryDelayMs: 800
        })
        const optimized = String(result.prompt || '').trim().slice(0, this.safeMaxLength)
        if (!optimized) throw new Error('AI 未返回可用的优化提示词')
        if (String(this.value || '') !== sourceValue) {
          this.statusText = '输入内容已变化，本次优化结果未覆盖'
          this.notify(this.statusText, 'warning')
          return
        }
        this.originalValue = sourceValue
        this.optimizedValue = optimized
        this.applyingValue = true
        this.$emit('input', optimized)
        this.$emit('optimized', { original: sourceValue, optimized, degraded: Boolean(result.degraded) })
        this.statusText = result.degraded
          ? (result.message || '图片理解过程中出现问题，本次已先根据文字做了浅层优化，结果未结合图片细节。')
          : '提示词已优化，可继续修改或撤销'
        this.notify(this.statusText, result.degraded ? 'warning' : 'success')
        this.$nextTick(() => { this.applyingValue = false })
      } catch (error) {
        this.statusText = error.message || 'AI 优化提示词失败，请稍后重试'
        this.notify(this.statusText, 'error')
        this.$emit('error', error)
      } finally {
        this.optimizing = false
        this.$emit('loading', false)
        this.cooldown = true
        if (this.cooldownTimer) window.clearTimeout(this.cooldownTimer)
        this.cooldownTimer = window.setTimeout(() => {
          this.cooldown = false
          this.cooldownTimer = null
        }, 1200)
      }
    },
    undo() {
      if (!this.canUndo || this.disabled || this.optimizing) return
      const original = this.originalValue
      this.applyingValue = true
      this.$emit('input', original)
      this.$emit('undone', { value: original })
      this.statusText = '已撤销 AI 提示词优化'
      this.notify(this.statusText, 'success')
      this.$nextTick(() => {
        this.applyingValue = false
        this.clearUndo()
      })
    }
  }
}
</script>

<style scoped>
.ai-prompt-optimizer.ai-prompt-optimizer{width:100%;display:inline-flex;align-items:center;justify-content:flex-end;gap:6px;margin:7px 0 0;color:inherit;font-size:inherit;font-style:normal;font-weight:400}.ai-prompt-optimizer button{min-height:36px;display:inline-flex;align-items:center;justify-content:center;gap:6px;border-radius:6px;padding:0 12px;font-family:inherit;font-size:10px;font-weight:700;line-height:1;cursor:pointer;touch-action:manipulation;transition:border-color .18s ease,background-color .18s ease,color .18s ease,box-shadow .18s ease,transform .18s ease}.optimize-button{border:1px solid #a9c1ef;background:#edf4ff;color:#245fc5;box-shadow:0 4px 12px rgba(37,99,235,.08)}.optimize-button:hover:not(:disabled){border-color:#6f96e7;background:#e3edff;box-shadow:0 6px 16px rgba(37,99,235,.14);transform:translateY(-1px)}.undo-button{border:1px solid #d9e1dc;background:#fff;color:#657169}.undo-button:hover:not(:disabled){border-color:#9baaa1;color:#344139}.ai-prompt-optimizer button:focus-visible{outline:2px solid #245fc5;outline-offset:2px}.ai-prompt-optimizer button:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;transform:none}.visually-hidden{width:1px;height:1px;overflow:hidden;position:absolute;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}@media(max-width:767px),(pointer:coarse){.ai-prompt-optimizer button{min-height:44px;padding-right:14px;padding-left:14px;font-size:12px}}@media(prefers-reduced-motion:reduce){.ai-prompt-optimizer button{transition:none}.optimize-button:hover:not(:disabled){transform:none}}
</style>
